import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { ArrowDown, ArrowUp, CheckCircle2, Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { ColorPicker } from '@components/ColorPicker';
import boardService from '@services/boardService';
import type { Board, BoardColumn, BoardColumnFormData, BoardFormData, BoardType } from '@/types/board';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { colorClasses, DEFAULT_COLOR } from '@/utils/colors';

interface Props {
  /** `null` = quadro novo. */
  boardId: number | null;
  onClose: () => void;
  /** Depois de criar, o editor reabre no quadro novo (para as colunas). */
  onCreated: (id: number) => void;
  onError: (err: unknown) => void;
}

type Tab = 'data' | 'columns';

const labelCls = 'text-label block mb-1';

const emptyForm = (): BoardFormData => ({
  name: '',
  description: '',
  board_type: 'daily',
  is_main: false,
  start_date: '',
  end_date: '',
  color: DEFAULT_COLOR,
  is_archived: false,
});

export const BoardEditorModal: React.FC<Props> = ({ boardId, onClose, onCreated, onError }) => {
  const isNew = boardId === null;
  const [tab, setTab] = useState<Tab>('data');
  const [board, setBoard] = useState<Board | null>(null);
  const [form, setForm] = useState<BoardFormData>(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (boardId === null) return;
    try {
      const b = await boardService.getById(boardId);
      setBoard(b);
      setForm({
        name: b.name,
        description: b.description ?? '',
        board_type: b.board_type,
        is_main: b.is_main,
        start_date: b.start_date ?? '',
        end_date: b.end_date ?? '',
        color: b.color,
        is_archived: b.is_archived,
      });
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [boardId, onError]);

  useEffect(() => {
    reload();
  }, [reload]);

  const flash = (msg: string) => {
    setSaved(msg);
    window.setTimeout(() => setSaved(null), 2500);
  };

  const set = <K extends keyof BoardFormData>(key: K, value: BoardFormData[K]) => setForm((f) => ({ ...f, [key]: value }));

  const dateError =
    form.start_date && form.end_date && form.end_date < form.start_date ? 'O fim não pode ser antes do início.' : null;

  const saveData = async () => {
    if (!form.name.trim() || dateError) return;
    const payload: BoardFormData = {
      ...form,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      is_main: form.board_type === 'daily' ? form.is_main : false,
    };
    setSaving(true);
    try {
      if (boardId === null) {
        const created = await boardService.create(payload);
        onCreated(created.id);
      } else {
        await boardService.update(boardId, payload);
        await reload();
        flash('Dados salvos.');
      }
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="xl" showCloseButton={false} closeOnBackdrop={!saving}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          {board && <span className={clsx('w-3 h-3 rounded-full', colorClasses(board.color).swatch)} />}
          <h2 className="text-h3 truncate">{isNew ? 'Novo quadro' : board?.name ?? 'Quadro'}</h2>
          {saved && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={12} /> {saved}
            </span>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Fechar" className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100">
          <X size={18} />
        </button>
      </div>

      {!isNew && (
        <div className="px-6 border-b border-slate-200 flex gap-6">
          {(
            [
              ['data', 'Dados'],
              ['columns', 'Colunas'],
            ] as [Tab, string][]
          ).map(([t, label]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={clsx(
                'py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="px-6 py-4 max-h-[65vh] overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 size={20} className="animate-spin mr-2" /> Carregando…
          </div>
        ) : tab === 'data' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelCls}>
                Nome <span className="text-red-500">*</span>
              </label>
              <input className="input-field" maxLength={150} value={form.name} onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Descrição</label>
              <textarea
                className="textarea-field"
                rows={2}
                maxLength={2000}
                value={form.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Tipo</label>
              <select
                className="select-field"
                value={form.board_type}
                onChange={(e) => set('board_type', e.target.value as BoardType)}
              >
                {(Object.keys(BOARD_TYPE_LABELS) as BoardType[]).map((t) => (
                  <option key={t} value={t}>
                    {BOARD_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
              <p className="text-small mt-1">
                {form.board_type === 'daily'
                  ? 'Quadro das tarefas do dia a dia; um deles é o principal.'
                  : 'Projeto com período: aparece no cronograma do calendário.'}
              </p>
            </div>
            <div className="flex flex-col justify-center gap-2">
              {form.board_type === 'daily' && (
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox-field"
                    checked={form.is_main}
                    onChange={(e) => set('is_main', e.target.checked)}
                  />
                  Quadro principal (abre por padrão no Kanban)
                </label>
              )}
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox-field"
                  checked={form.is_archived}
                  onChange={(e) => set('is_archived', e.target.checked)}
                />
                Arquivado
              </label>
            </div>
            {form.board_type === 'project' && (
              <>
                <div>
                  <label className={labelCls}>Início do período</label>
                  <DateInputBR
                    className="input-field w-full"
                    value={form.start_date ?? ''}
                    onChange={(v) => set('start_date', v)}
                  />
                </div>
                <div>
                  <label className={labelCls}>Fim do período</label>
                  <DateInputBR
                    className="input-field w-full"
                    value={form.end_date ?? ''}
                    min={form.start_date || undefined}
                    onChange={(v) => set('end_date', v)}
                  />
                </div>
                <p className="md:col-span-2 text-small -mt-2">O período aparece no cronograma do calendário.</p>
              </>
            )}
            <div className="md:col-span-2">
              <label className={labelCls}>Cor</label>
              <ColorPicker value={form.color ?? DEFAULT_COLOR} onChange={(c) => set('color', c)} />
            </div>
            {dateError && <p className="md:col-span-2 text-sm text-red-600">{dateError}</p>}
            {isNew && (
              <p className="md:col-span-2 text-small">
                O quadro nasce com as colunas "A fazer / Em andamento / Concluído"; depois de criar você pode ajustar as
                colunas.
              </p>
            )}
          </div>
        ) : tab === 'columns' && board ? (
          <ColumnsEditor board={board} onChanged={reload} onError={onError} />
        ) : null}
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Fechar
        </Button>
        {tab === 'data' && (
          <Button variant="primary" icon={<Save />} onClick={saveData} loading={saving} disabled={!form.name.trim() || !!dateError}>
            {isNew ? 'Criar quadro' : 'Salvar dados'}
          </Button>
        )}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------- colunas

interface ColumnDraft {
  name: string;
  color: string;
  is_done_column: boolean;
  wip_limit: string;
}

const draftOf = (c: BoardColumn): ColumnDraft => ({
  name: c.name,
  color: c.color,
  is_done_column: c.is_done_column,
  wip_limit: c.wip_limit ? String(c.wip_limit) : '',
});

const toPayload = (d: ColumnDraft): BoardColumnFormData => ({
  name: d.name.trim(),
  color: d.color,
  is_done_column: d.is_done_column,
  wip_limit: d.wip_limit && Number(d.wip_limit) > 0 ? Number(d.wip_limit) : null,
});

const ColumnsEditor: React.FC<{ board: Board; onChanged: () => Promise<void>; onError: (e: unknown) => void }> = ({
  board,
  onChanged,
  onError,
}) => {
  const columns = board.columns ?? [];
  const [drafts, setDrafts] = useState<Record<number, ColumnDraft>>({});
  const [busy, setBusy] = useState<number | 'new' | 'order' | null>(null);
  const [newCol, setNewCol] = useState<ColumnDraft>({ name: '', color: DEFAULT_COLOR, is_done_column: false, wip_limit: '' });
  const [deleting, setDeleting] = useState<BoardColumn | null>(null);
  const [moveTo, setMoveTo] = useState('');

  useEffect(() => {
    setDrafts(Object.fromEntries(columns.map((c) => [c.id, draftOf(c)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  const isDirty = (c: BoardColumn) => {
    const d = drafts[c.id];
    if (!d) return false;
    const o = draftOf(c);
    return d.name !== o.name || d.color !== o.color || d.is_done_column !== o.is_done_column || d.wip_limit !== o.wip_limit;
  };

  const patch = (id: number, p: Partial<ColumnDraft>) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...p } }));

  const saveColumn = async (c: BoardColumn) => {
    const d = drafts[c.id];
    if (!d || !d.name.trim()) return;
    setBusy(c.id);
    try {
      await boardService.updateColumn(board.id, c.id, toPayload(d));
      await onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(null);
    }
  };

  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= columns.length) return;
    const ids = columns.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusy('order');
    try {
      await boardService.reorderColumns(board.id, ids);
      await onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(null);
    }
  };

  const addColumn = async () => {
    if (!newCol.name.trim()) return;
    setBusy('new');
    try {
      await boardService.createColumn(board.id, toPayload(newCol));
      setNewCol({ name: '', color: DEFAULT_COLOR, is_done_column: false, wip_limit: '' });
      await onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(deleting.id);
    try {
      await boardService.deleteColumn(board.id, deleting.id, moveTo ? Number(moveTo) : undefined);
      setDeleting(null);
      setMoveTo('');
      await onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-small">
        A ordem aqui é a ordem das colunas no Kanban. Mover uma atividade para uma coluna marcada como "concluído" a conclui.
      </p>
      {columns.map((c, i) => {
        const d = drafts[c.id] ?? draftOf(c);
        return (
          <div key={c.id} className={clsx('border rounded-lg p-3 border-l-4 space-y-2', colorClasses(d.color).borderLeft)}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0 || busy !== null}
                  className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  title="Mover para a esquerda"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === columns.length - 1 || busy !== null}
                  className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  title="Mover para a direita"
                >
                  <ArrowDown size={14} />
                </button>
              </div>
              <input
                className="input-field flex-1 min-w-[160px]"
                maxLength={100}
                value={d.name}
                onChange={(e) => patch(c.id, { name: e.target.value })}
              />
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  className="checkbox-field"
                  checked={d.is_done_column}
                  onChange={(e) => patch(c.id, { is_done_column: e.target.checked })}
                />
                Concluído
              </label>
              <input
                type="number"
                min={1}
                className="input-field w-24"
                placeholder="WIP"
                title="Limite WIP (vazio = sem limite)"
                value={d.wip_limit}
                onChange={(e) => patch(c.id, { wip_limit: e.target.value })}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveColumn(c)}
                disabled={!isDirty(c) || !d.name.trim() || busy !== null}
                loading={busy === c.id}
              >
                Salvar
              </Button>
              <button
                type="button"
                onClick={() => setDeleting(c)}
                disabled={busy !== null || columns.length <= 1}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-30"
                title="Excluir coluna"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <ColorPicker size="sm" value={d.color} onChange={(color) => patch(c.id, { color })} />
          </div>
        );
      })}

      <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2">
        <p className="text-sm font-medium text-slate-700">Nova coluna</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input-field flex-1 min-w-[160px]"
            maxLength={100}
            placeholder="Nome da coluna"
            value={newCol.name}
            onChange={(e) => setNewCol({ ...newCol, name: e.target.value })}
          />
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={newCol.is_done_column}
              onChange={(e) => setNewCol({ ...newCol, is_done_column: e.target.checked })}
            />
            Concluído
          </label>
          <input
            type="number"
            min={1}
            className="input-field w-24"
            placeholder="WIP"
            value={newCol.wip_limit}
            onChange={(e) => setNewCol({ ...newCol, wip_limit: e.target.value })}
          />
          <Button variant="secondary" size="sm" icon={<Plus />} onClick={addColumn} loading={busy === 'new'} disabled={!newCol.name.trim()}>
            Adicionar
          </Button>
        </div>
        <ColorPicker size="sm" value={newCol.color} onChange={(color) => setNewCol({ ...newCol, color })} />
      </div>

      {deleting && (
        <Modal isOpen onClose={() => setDeleting(null)} size="sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">Excluir coluna</h2>
          </div>
          <div className="px-6 py-4 space-y-3">
            <p className="text-sm text-slate-700">
              Excluir a coluna <strong>{deleting.name}</strong>? Se ela tiver atividades, escolha para onde movê-las.
            </p>
            <div>
              <label className={labelCls}>Mover atividades para</label>
              <select className="select-field" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                <option value="">— (coluna vazia)</option>
                {columns
                  .filter((c) => c.id !== deleting.id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={busy === deleting.id}>
              Excluir
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
